import { FaLaptopCode } from 'react-icons/fa6';
import { HiBriefcase, HiClock, HiCurrencyRupee, HiLocationMarker } from 'react-icons/hi';
import { Img } from 'react-image';
import ReactQuill from 'react-quill';
import ReadMoreText from 'src/components/shared/ReadMoreText';
import { useUserContext } from 'src/context/UserProvider';
import { PositionRequirement, RecommendationsForScrapedPositionDocument, WorkingMode } from 'types';

interface Props {
	workingMode: string;
	workingExp: string;
	minSalary: number;
	maxSalary: number;
	location: string;
	jobType: string;
	description: string;
	positionRequirenment?: PositionRequirement;
	minInternshipSalary?: number;
	maxInternshipSalary?: number;
	internshipDuration?: number;
	hasBond?: boolean;
	bondDetails?: string;
	skills?: string[];
	scrapedPosition?: RecommendationsForScrapedPositionDocument;
}

const JobDescription = ({
	positionRequirenment,
	workingMode,
	workingExp,
	minSalary,
	maxSalary,
	location,
	jobType,
	description,
	minInternshipSalary,
	maxInternshipSalary,
	internshipDuration,
	hasBond,
	bondDetails,
	skills,
	scrapedPosition,
}: Props) => {
	const { user } = useUserContext();
	return (
		<>
			{hasBond ? (
				<>
					<div className="relative top-[-20px] mx-[-24px] bg-[#FFF8E5] md:rounded-[6px] px-6 py-1 md:hidden max-h-[25px] md:max-w-max text-[#E57A00] dark:text-[#FACA15] text-xs font-[500] font-inter dark:bg-[#374151]">
						*This job includes bond agreement.
					</div>
				</>
			) : null}
			<div
				style={{
					border: '1px solid transparent',
				}}
				className="text-sm font-normal text-[#28293D] dark:text-[#EBEBF0]"
			>
				<>
					{workingMode === WorkingMode.INTERNSHIP_TO_FULL_TIME ? (
						<div className="bg-[#F2F2F5] dark:bg-[#111E2C] mx-[-24px] md:mx-0 mt-[-20px] md:mt-3 px-[24px] md:px-[14px] py-[12px] md:rounded-[16px]">
							<div className="flex flex-col md:flex-row gap-[10px] lg:gap-[3rem]">
								<div className="flex-1">
									<p className="text-[#004FC4] dark:text-[#a7cbff] text-sm font-inter font-[500]">
										Job Type: {workingMode === 'InternshipToFullTime' ? 'Internship to Full-Time' : 'Internship'}
									</p>

									<div className="flex flex-wrap gap-x-[20%] md:gap-x-[16px] gap-y-[12px] pt-[7px] md:border-r border-[#CBCACA]">
										<div className="flex items-center">
											<HiBriefcase className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
											<div className="ml-1 flex items-center gap-1">Internship</div>
										</div>

										<div>
											<div className="flex items-center">
												<HiClock className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
												<div className="ml-1">{internshipDuration} months</div>
											</div>
										</div>

										{maxInternshipSalary ? (
											<div>
												<div className="flex items-center">
													<HiCurrencyRupee className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
													<div className="ml-1">
														{`${
															minInternshipSalary === maxInternshipSalary
																? maxInternshipSalary
																: `${minInternshipSalary} - ${maxInternshipSalary}`
														}`}{' '}
														K per month
													</div>
												</div>
											</div>
										) : null}

										<div className="flex items-center">
											<HiLocationMarker className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
											<div className="ml-1 flex items-center gap-1 flex-wrap">
												{location}
												<div className="font-inter font-[500] text-[15px] leading-[16px] text-[#8F90A6] dark:text-[#C7C9D9]">
													•
												</div>
												<span>{jobType}</span>
											</div>
										</div>
									</div>
								</div>

								<div className="flex-1">
									<p className="text-[#004FC4] dark:text-[#a7cbff] text-sm font-inter font-[500]">
										Conversion to Full - Time{' '}
									</p>

									<div className="flex flex-wrap gap-x-[20%] md:gap-x-[16px] gap-y-[12px] pt-[7px]">
										<div className="flex items-center">
											<HiBriefcase className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
											<div className="ml-1 flex items-center gap-1">Full - time</div>
										</div>

										<div>
											<div className="flex items-center">
												<HiCurrencyRupee className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
												<div className="ml-1">
													{`${minSalary === maxSalary ? maxSalary : `${minSalary} - ${maxSalary}`}`} LPA
												</div>
											</div>
										</div>

										<div className="flex items-center">
											<HiLocationMarker className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
											<div className="ml-1 flex items-center gap-1 flex-wrap">
												{location}
												<div className="font-inter font-[500] text-[15px] leading-[16px] text-[#8F90A6] dark:text-[#C7C9D9]">
													•
												</div>
												<span>{jobType}</span>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					) : (
						<div className="mx-[-24px] md:mx-0 mt-[-20px] md:mt-0 px-[24px] md:px-[0px] py-[12px] md:rounded-[16px]">
							<div className="flex items-center">
								<HiBriefcase className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
								<div className="ml-1 flex items-center gap-1">
									{workingMode ? (
										<>
											{workingMode === 'FullTime'
												? 'Full-Time'
												: workingMode === 'InternshipToFullTime'
												  ? 'Internship + Full-Time'
												  : workingMode}
											{workingExp ? (
												<div className="font-inter font-[500] text-[15px] leading-[16px] text-[#8F90A6] dark:text-[#C7C9D9]">
													•
												</div>
											) : null}
										</>
									) : null}
									<span>{workingExp}</span>
								</div>
							</div>

							{minSalary || maxSalary ? (
								<div className="mt-[12px] flex items-center">
									<HiCurrencyRupee className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
									<div className="ml-1">
										{`${minSalary === maxSalary ? maxSalary : `${minSalary} - ${maxSalary}`}`} LPA
									</div>
								</div>
							) : null}

							{scrapedPosition?.techStack ? (
								<div className="mt-[12px] flex items-center">
									<FaLaptopCode className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
									<div className="ml-1">{scrapedPosition.techStack}</div>
								</div>
							) : null}

							{scrapedPosition?.salary ? (
								<div className="mt-[12px] flex items-center">
									<HiCurrencyRupee className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
									<div className="ml-1">{scrapedPosition.salary}</div>
								</div>
							) : null}

							{location || jobType ? (
								<div className="mt-[12px] flex items-center">
									<HiLocationMarker className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
									<div className="ml-1 flex items-center gap-1">
										{location ? (
											<>
												{location}
												{jobType ? (
													<div className="font-inter font-[500] text-[15px] leading-[16px] text-[#8F90A6] dark:text-[#C7C9D9]">
														•
													</div>
												) : null}
											</>
										) : null}
										<span>{jobType}</span>
									</div>
								</div>
							) : null}

							{internshipDuration ? (
								<div className="flex items-center mt-[12px]">
									<HiClock className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
									<div className="ml-1">{internshipDuration} months</div>
								</div>
							) : null}
						</div>
					)}
				</>

				{!user ? (
					<div className="flex flex-col gap-3 my-4">
						<div>
							<p className="text-sm font-medium">Must Have Skills</p>
							<div className="flex flex-wrap gap-2 mt-2">
								{positionRequirenment?.skillThresholds?.map(elem => (
									<div className="border rounded-[5px] py-1 px-2 bg-[#242C40] text-[#E4E4EB]">{elem.displaySkill}</div>
								))}
							</div>
						</div>
						{positionRequirenment?.goodToHaveskills?.length ? (
							<div>
								<p className="text-sm font-medium">Good To Have Skills</p>
								<div className="flex flex-wrap gap-2 mt-2">
									{positionRequirenment?.goodToHaveskills?.map(elem => (
										<div className="border rounded-[5px] py-1 px-2 bg-[#242C40] text-[#E4E4EB]">{elem.name}</div>
									))}
								</div>
							</div>
						) : null}
					</div>
				) : null}

				{scrapedPosition?.jobLink ? (
					<div className="flex gap-2 my-4">
						<p className="text-sm font-medium">Job Posting Link:</p>
						<div
							onClick={() => window.open(`${scrapedPosition?.jobLink}`, '_blank')}
							className={`flex items-center gap-2 text-base font-medium text-[#6698FA] cursor-pointer dark:text-inherit`}
						>
							<span className="hover:underline">here</span>
							<span>
								<Img
									loading="lazy"
									className=""
									src="/assets/externalLinkIcon.svg"
									alt="externalLinkIcon"
									width="16"
									height="16"
								/>
							</span>
						</div>
					</div>
				) : null}

				{scrapedPosition?.jobPortal ? (
					<div className="flex gap-2 my-4">
						<p className="text-sm font-medium">Job Portal:</p>
						<div className={`flex items-center gap-2 text-base font-medium  dark:text-inherit`}>
							<span className="text-sm">{scrapedPosition.jobPortal}</span>
						</div>
					</div>
				) : null}

				{skills?.length ? (
					<div className="my-2">
						<p className="text-sm font-medium">Skills Required:</p>
						<div className="flex flex-wrap gap-2 mt-2">
							{skills?.map(elem => <div className="border rounded-[5px] py-1 px-2 ">{elem}</div>)}
						</div>
					</div>
				) : null}

				{hasBond && bondDetails ? (
					<div className="mb-[24px] mt-[20px] text-[#28293D] dark:text-[#C6C9D9] sm:mb-0">
						<p className="text-sm font-medium mb-[12px]">Bond Details</p>
						<div className="prose dark:prose-invert text-sm mt-[10px]">
							<ReadMoreText work_description={bondDetails} maxChars={bondDetails?.length > 100 ? 200 : 50} />
						</div>
					</div>
				) : null}

				<div className="quill-container position-drawer-quill-editor mb-[24px] mt-[30px] text-[#28293D] dark:text-[#C6C9D9] sm:mb-0">
					<p className="text-sm font-medium mb-[12px]">Job descriptions</p>
					<ReactQuill
						modules={{
							toolbar: false,
						}}
						readOnly={true}
						value={description}
					/>
				</div>
			</div>
		</>
	);
};

export default JobDescription;
